import { useContext, useMemo } from 'react'

import { type SanityFrameBuilderType } from '@frame-builder/data/sanity/queries/types'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderSelectedStateContext } from '@frame-builder/contexts/state-context'
import { clamp } from './helpers'
import { getHasPassepartout } from './passepartout'
import { getIsStepEnabled } from './steps'
import {
  type FrameBuilderProduct,
  type FrameBuilderStep,
  type ValidationErrorMessages,
} from './types'

/**
 * Determines whether all frame parts are present in product data.
 */
export const getIsFrameComplete = (
  frameBuilderType: SanityFrameBuilderType,
  product: FrameBuilderProduct,
) => {
  if (!product) {
    return false
  }

  const hasPassepartout = getHasPassepartout(product.passepartout)

  if (frameBuilderType === 'passepartout') {
    return (
      product.artwork.width > 0 && product.artwork.height > 0 && hasPassepartout
    )
  }

  const isFloatingFrame = !!product.profile?.floatingFrame

  if (isFloatingFrame) {
    return (
      product.artwork.width > 0 &&
      product.artwork.height > 0 &&
      !!product.profile &&
      !!product.texture
    )
  }

  return (
    product.artwork.width > 0 &&
    product.artwork.height > 0 &&
    !!product.profile &&
    !!product.texture &&
    (!product.passepartout.color || hasPassepartout) &&
    !!product.glass
  )
}

export const useFrameCompletionPercentage = (
  frameBuilderType: SanityFrameBuilderType,
  validationErrorMessages: ValidationErrorMessages,
) => {
  const { selectedState } = useContext(FrameBuilderSelectedStateContext)
  const { frameBuilderConfiguration } = useContext(FrameBuilderSettingsContext)

  return useMemo(() => {
    const stepIsValidMap: Record<FrameBuilderStep, boolean> = {
      artworkSize: !validationErrorMessages?.artowkSizePreview,
      artworkImage: true,
      frameStyle:
        !validationErrorMessages?.frameStyleProfilePreview &&
        !validationErrorMessages?.frameStyleTexturePreview,
      passepartout:
        !validationErrorMessages?.passepartoutColorPreview &&
        !validationErrorMessages?.passepartoutSizePreview,
      glass: !validationErrorMessages?.glassTypePreview,
      spacer:
        !validationErrorMessages?.spacerSizePreview &&
        !validationErrorMessages?.spacerColorPreview,
    }
    const enabledStepIsValidMap = Object.fromEntries(
      Object.entries(stepIsValidMap).filter(([step]) => {
        return getIsStepEnabled(
          frameBuilderType,
          frameBuilderConfiguration,
          selectedState,
          step as FrameBuilderStep,
        )
      }),
    )

    const enabledStepCount = Object.keys(enabledStepIsValidMap).length
    const enabledStepValidCount = Object.values(enabledStepIsValidMap).filter(
      Boolean,
    ).length
    const frameCompletionPercentage = Math.round(
      (enabledStepValidCount / enabledStepCount) * 100,
    )

    return clamp(frameCompletionPercentage, 0, 100)
  }, [
    frameBuilderConfiguration,
    frameBuilderType,
    selectedState,
    validationErrorMessages,
  ])
}
